import { Box } from '@mui/material';
import React, { Fragment, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { PageTitle } from '../../../components/PageTitle';
import { useTranslation } from 'react-i18next';
import { Breadcrumbs } from '../../../components/Breadcrumbs';
import { Crumb } from '../../../components/Breadcrumbs/interfaces';
import { enqueueSnackbar } from 'notistack';
import { AxiosError } from 'axios';
import { LoadingLayover } from '../../../components/LoadingLayover';
import { useGetContact } from '../../../reactQuery/contacts';
import {
  ActivityLogTab,
  DetailsTab,
  TabControl,
} from '../../../components/tabs';
import { DetailsTabPanel } from './tabs/DetailsTabPanel';
import { ActivityLogTabPanel } from './tabs/ActivityLogTabPanel';
import { OpportunitiesTabPanel } from '../../../components/tabs/tab-panels/OpportunitiesTabPanel';
import { DATA_TABLE_STORAGE_KEYS } from '../../../constants';
import { OpportunitiesTab } from '../../../components/tabs/tabs/OpportunitiesTab';

export const ContactDetails: React.FC = (): JSX.Element => {
  const { t } = useTranslation();

  const navigate = useNavigate();
  const params = useParams();

  const [currentTab, setCurrentTab] = useState<number>(0);

  const contactId = Number(params.id);
  const crumbs: Crumb[] = [
    {
      title: t('breadcrumbs.overview'),
      link: '/contacts',
    },
  ];

  const { data: contact, isLoading, error } = useGetContact(contactId);

  const handleContactNotFound = (): void => {
    enqueueSnackbar(
      t('errors.resourceNotFound', {
        resource: 'contact',
      }),
      {
        variant: 'error',
      }
    );
    navigate('/contacts');
  };

  useEffect(() => {
    if (Number.isNaN(contactId)) {
      handleContactNotFound();
    } else if (error) {
      const axiosError = error as AxiosError;

      if (axiosError.response?.status === 404) {
        handleContactNotFound();
      } else {
        enqueueSnackbar(t('errors.somethingWentWrong'), {
          variant: 'error',
        });
      }
    }
  }, [contactId, error, navigate]);

  if (isLoading) {
    return <LoadingLayover />;
  }

  if (!contact) {
    navigate('/contacts');
    return <Box>Error</Box>;
  }

  return (
    <Fragment>
      <PageTitle title={t('contacts:title')} space={'double'}>
        <Breadcrumbs
          crumbs={crumbs}
          current={`${contact.firstName} ${contact.lastName}`}
        />
      </PageTitle>

      <TabControl value={currentTab} onChange={(value) => setCurrentTab(value)}>
        <DetailsTab />
        <ActivityLogTab />
        <OpportunitiesTab />
      </TabControl>

      <DetailsTabPanel value={currentTab} index={0} contact={contact} />
      <ActivityLogTabPanel
        value={currentTab}
        index={1}
        contactId={contact.id}
      />
      <OpportunitiesTabPanel
        value={currentTab}
        index={2}
        filter="contact"
        id={contact.id}
        storageKey={DATA_TABLE_STORAGE_KEYS.contactOpportunitiesOverview}
      />
    </Fragment>
  );
};
